/// Cookie / storage consent state for ePrivacy + GDPR compliance.
///
/// Two values:
///   - 'all'       — user opted in to analytics + helpdesk widget.
///   - 'essential' — user wants only what's needed for the app to
///                   work (Firebase Auth, App Check, RTDB
///                   presence, Stripe at point-of-purchase, and
///                   localStorage for UI preferences). No GA4, no
///                   Beacon helpdesk loader.
///
/// `null` from `getConsent()` means the user hasn't chosen yet —
/// the banner is shown until they pick. We deliberately do NOT
/// auto-pick or default to 'all' — that would bypass the law.
///
/// The legacy `flatout.web.analyticsOptOut = '1'` key is honored as
/// "consent withdrawn" for users who already opted out before the
/// banner shipped. Setting consent to 'all' clears it; setting to
/// 'essential' sets it (so existing analytics-checking code still
/// works without changes).

const CONSENT_KEY = 'flatout.web.consent.v1'
const LEGACY_ANALYTICS_OPT_OUT_KEY = 'flatout.web.analyticsOptOut'

export type ConsentLevel = 'all' | 'essential'

const listeners = new Set<(level: ConsentLevel) => void>()

export function getConsent(): ConsentLevel | null {
  try {
    const v = window.localStorage.getItem(CONSENT_KEY)
    if (v === 'all' || v === 'essential') return v
    // Pre-banner users who flipped the analytics toggle off get
    // mapped to 'essential' so the gate behaves the same.
    if (window.localStorage.getItem(LEGACY_ANALYTICS_OPT_OUT_KEY) === '1') {
      return 'essential'
    }
    return null
  } catch {
    return null
  }
}

export function setConsent(level: ConsentLevel): void {
  try {
    window.localStorage.setItem(CONSENT_KEY, level)
    if (level === 'all') {
      window.localStorage.removeItem(LEGACY_ANALYTICS_OPT_OUT_KEY)
    } else {
      window.localStorage.setItem(LEGACY_ANALYTICS_OPT_OUT_KEY, '1')
    }
  } catch {
    // localStorage unavailable (private mode strict) — listeners
    // still fire, the value just won't persist.
  }
  for (const cb of listeners) cb(level)
}

/// Subscribe to consent changes. Used by the Beacon loader (load
/// on grant) and by the firebase/app analytics-init path (re-eval
/// availability when the user grants/withdraws). Returns the
/// unsubscribe.
export function onConsentChange(cb: (level: ConsentLevel) => void): () => void {
  listeners.add(cb)
  return () => {
    listeners.delete(cb)
  }
}

export function hasAnalyticsConsent(): boolean {
  return getConsent() === 'all'
}

export function hasHelpdeskConsent(): boolean {
  return getConsent() === 'all'
}
